import { getAssetChannelLabel, getAssetLabel } from 'src/data/asset';
import {
  convertRewriteToolToSuggestTool,
  doesToolSupportsSubTemplates,
  GENERATION_TOOL,
  type GenerationTool,
  getToolContentType,
  getToolDescription,
  getToolLabel,
  getToolTitle,
  isBasicContentImproverTool,
  isFreestyleGenerationTool,
  isImageGenerationTool,
  isMixtureTool,
  isMultiPromptTool,
  isRewriteGenerationTool,
} from 'src/data/generationTool';
import { type MultiPromptTemplate } from 'src/data/template';
import type { ID } from 'src/lib/collection';
import { CHARACTER_BULLET } from 'src/lib/string';

import {
  AI_GENERATION_MODE,
  AI_RECOMPOSE_MODE,
  GENERATION_SOURCE_TYPE,
  type GenerationSourceType,
} from './const';
import type { GenerationRequest, RecomposeRequest, SuggestionRequest } from './types';

const describeSource = (sourceType: GenerationSourceType): string => {
  switch (sourceType) {
    case GENERATION_SOURCE_TYPE.improveListScore:
      return 'Content improver';
    case GENERATION_SOURCE_TYPE.changeTov:
      return 'More like this - change tone of voice';
    default:
      return '';
  }
};

const formatToolWithSubTemplatesTitle = (
  request: GenerationRequest,
  multiTemplate?: MultiPromptTemplate
): string => {
  const baseName = multiTemplate?.name || getToolTitle(request?.generationTool);
  return `${baseName} - Prompt`;
};

export const getGenerationResultDescription = (
  request: GenerationRequest,
  multiTemplate?: MultiPromptTemplate
) => {
  const { generationTool, assetType, sourceType } = request;

  const isRewriteTool = isRewriteGenerationTool(generationTool);

  const alignedTool = convertRewriteToolToSuggestTool(generationTool);
  let title = '';
  let description = '';

  if (request.chainGenerationTool) {
    title = getToolTitle(request.chainGenerationTool) || '';
    if (doesToolSupportsSubTemplates(request?.chainGenerationTool)) {
      title = formatToolWithSubTemplatesTitle(request, multiTemplate);
    }
    // new "improve sections" results
    if (isSectionRecomposeResult(request)) {
      if (isBasicContentImproverTool(request.chainGenerationTool)) {
        title = `Content improver ${CHARACTER_BULLET} General text`;
      } else {
        const isMixture = isMixtureTool(request.chainGenerationTool);
        const chainToolName = isMixture
          ? getToolContentType(request.chainGenerationTool)
          : getToolLabel(request.chainGenerationTool);

        title = `Content improver ${CHARACTER_BULLET} ${chainToolName}`;
      }
    } else if (isFreestyleGenerationTool(request.chainGenerationTool)) {
      title = getToolContentType(request.chainGenerationTool) || '';
    }

    // old "recompose" results with "basic content improver" tool
  } else if (isBasicContentImproverTool(alignedTool)) {
    title = `Content improver ${CHARACTER_BULLET} General text`;

    // other tools
  } else if (alignedTool) {
    if (isRewriteTool) {
      title = `Content improver ${CHARACTER_BULLET} `;
    } else {
      title = '';
    }
    title += getToolContentType(alignedTool) || '';

    if (doesToolSupportsSubTemplates(request?.generationTool)) {
      title = formatToolWithSubTemplatesTitle(request, multiTemplate);
    }
    description = getToolDescription(alignedTool) || '';
    if (
      !isMixtureTool(alignedTool) &&
      !isMultiPromptTool(alignedTool) &&
      !isImageGenerationTool(alignedTool) &&
      !isFreestyleGenerationTool(alignedTool) &&
      !isRewriteTool
    ) {
      title += ` - ${getAssetLabel(assetType)}`;

      if (doesToolSupportsSubTemplates(request?.generationTool)) {
        const baseName = multiTemplate?.name || getToolTitle(request?.generationTool);
        title = `${baseName} - Prompt`;
      }
    }
  } else {
    if (doesToolSupportsSubTemplates(request?.generationTool)) {
      title = formatToolWithSubTemplatesTitle(request, multiTemplate);
    } else {
      title = getAssetChannelLabel(assetType) || '';
    }
    description = getAssetLabel(assetType) || '';
  }

  if (!description && isSuggestGenerationResult(request) && request.groupId) {
    description = getAssetLabel(assetType) || '';
  }

  const source = describeSource(sourceType);

  return {
    title,
    description,
    source,
  };
};

export const isSuggestGenerationResult = (
  result: GenerationRequest
): result is SuggestionRequest => (result ? Object.hasOwn(result, 'suggestionMode') : false);

export const isRecomposeGenerationResult = (
  result: GenerationRequest
): result is RecomposeRequest => (result ? Object.hasOwn(result, 'recomposeMode') : false);

export const getGenerationResultListId = (result: GenerationRequest): string =>
  isSuggestGenerationResult(result)
    ? `${AI_GENERATION_MODE.suggest}_${result?.id}`
    : `${AI_GENERATION_MODE.recompose}_${result?.id}`;

export const getGenerationRequestTool = (request: GenerationRequest): GenerationTool => {
  if (isSectionRecomposeResult(request)) {
    return GENERATION_TOOL.basicContentImprover;
  }
  if (isSuggestGenerationResult(request)) {
    return request.chainGenerationTool || request.generationTool;
  }
  return request.generationTool;
};

export const isPartialGenerationResultId = (id: ID): boolean => parseInt(String(id)) < 0;

export const isSectionRecomposeResult = (result: GenerationRequest) => {
  return isRecomposeGenerationResult(result) && result.chainId != null;
};

export const isFailedRecomposeGenerationResult = (result: GenerationRequest): boolean => {
  // consider only present "recompose" results
  if (!result) {
    return false;
  }
  if (!isRecomposeGenerationResult(result)) {
    return false;
  }

  const { variations } = result;

  // if there are no variations, consider it failed
  if (variations.length === 0) {
    return true;
  }

  // if there is only one variation, check its properties
  if (variations.length === 1) {
    const firstVariation = variations[0];

    // if the recompose mode is "keep as is", consider it "not failed", because user wanted to keep the original content
    if (firstVariation.recomposeMode === AI_RECOMPOSE_MODE.keepAsIs) {
      return false;
    }

    // if this is an "original" variation and has no sub-variations, consider it failed
    return !firstVariation?.originalVariationId && !firstVariation.variations?.length;
  }

  return false;
};
